import { useEffect, useMemo, useState } from 'react';

import { createAttendUser, updateAttendUser } from '@src/api/attend';
import { closeModal } from '@src/reduce/modals';
import getIsResponseFalse from '@src/utils/getIsResponseFalse';
import { trigger } from '@src/utils/globalEvents';
import { Button, message } from 'antd';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import FormAttendUser from './FormAttendUser';
import * as S from './styled';
import * as T from './type';

interface CreateAttendUserModalProps {
  initialFormData?: T.CreateAttendUser & { id: number };
  isEditable?: boolean;
  groupEventId: number;
}

const CreateAttendUserModal = ({
  initialFormData,
  isEditable,
  groupEventId,
}: CreateAttendUserModalProps) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<T.CreateAttendUser>({
    attendanceTime: initialFormData?.attendanceTime || '',
    name: initialFormData?.name || '',
    phone: initialFormData?.phone || '',
    email: initialFormData?.email || '',
    organization: initialFormData?.organization || '',
  });

  const close = () => {
    dispatch(closeModal('createAttendUserModal'));
  };

  const validationCheck = () => {
    if (!formData.name) {
      message.error('이름을 입력해주세요');
      return false;
    } else if (!formData.phone) {
      message.error('휴대폰번호를 입력해주세요');
      return false;
    }

    return true;
  };

  const createEvent = async () => {
    if (!validationCheck()) return;

    setIsLoading(true);
    const response = await createAttendUser({
      path: { groupEventId },
      data: {
        ...formData,
        organization: formData.organization || '',
        attendanceTime: formData.attendanceTime || dayjs().format('YYYY-MM-DD HH:mm'),
      },
    });

    setIsLoading(false);
    if (!response) return;

    message.success('출석자가 등록되었습니다.');

    trigger('SUCCESS_UPDATE_ATTEND_USER');
    close();
  };

  const editEvent = async () => {
    if (!initialFormData?.id) return;

    const response = await updateAttendUser({
      path: { groupEventId: groupEventId, groupEventMemberId: initialFormData.id },
      data: { ...formData },
    });

    if (getIsResponseFalse(response)) return;

    message.success('수정 되었습니다.');
    trigger('SUCCESS_UPDATE_ATTEND_USER');
    close();
  };

  const footBtns = useMemo(() => {
    return [
      <Button key='cancel' onClick={() => close()}>
        취소
      </Button>,
      !isEditable ? (
        <Button key='create' type='primary' loading={isLoading} onClick={() => createEvent()}>
          등록
        </Button>
      ) : (
        <Button key='edit' type='primary' loading={isLoading} onClick={() => editEvent()}>
          수정
        </Button>
      ),
    ];
  }, [createEvent, editEvent, close, isLoading]);

  return (
    <S.ModalContainer
      open={true}
      title={!isEditable ? '출석자 등록' : '출석자 수정'}
      footer={footBtns}
      width='500px'
      onCancel={close}>
      <FormAttendUser formData={formData} setFormData={setFormData} isEditable={!!isEditable} />
    </S.ModalContainer>
  );
};

export default CreateAttendUserModal;
